// 字段权限 / 敏感字段脱敏规则 Store (V2 权限基础设施 M1a)
//
// 设计:
//   - 字段维度: module + field
//   - 敏感度: L1 一般 / L2 内部 / L3 敏感 / L4 高敏 (国标 + 食品业自定义)
//   - 角色可见 / 可编辑 列表
//   - 场景化脱敏 (list 列表 / detail 详情 / export 导出 / approval 审批 / report 报表 / self 自助)
//   - noExport / audit 标记
//
// 持久化: UAT-FB#99 / ADR-001 (2026-06-17) — sys_kv 后端表, key='cfg:field_perm'

import { computed } from 'vue'
import { useBackendKv } from './_backendKv'

// ─── 类型 ─────────────────────────────────────────────────────────────────
export type SensitivityLevel = 'L1' | 'L2' | 'L3' | 'L4'

export const SENSITIVITY_META: Record<SensitivityLevel, { label: string; color: string; bg: string; desc: string; retentionDays: number }> = {
  L1: { label: 'L1 一般', color: '#16a34a', bg: '#dcfce7', desc: '一般信息 (姓名/工号/岗位等公开)', retentionDays: 180 },
  L2: { label: 'L2 内部', color: '#3b82f6', bg: '#dbeafe', desc: '内部信息 (部门历史/绩效等级)', retentionDays: 360 },
  L3: { label: 'L3 敏感', color: '#E89311', bg: '#fef3c7', desc: '敏感信息 (手机/邮箱/学历)', retentionDays: 1080 },
  L4: { label: 'L4 高敏', color: '#C8141E', bg: '#fee2e2', desc: '高敏信息 (身份证/银行卡/薪酬全额)', retentionDays: 2520 },
}

export type MaskRule = 'NONE' | 'FULL' | 'MIDDLE' | 'TAIL' | 'HEAD' | 'CUSTOM' | 'BAND_ONLY'

export const MASK_RULE_META: Record<MaskRule, { label: string; color: string; example: string }> = {
  NONE:      { label: '不脱敏',     color: '#16a34a', example: '完整显示' },
  FULL:      { label: '全脱敏',     color: '#C8141E', example: '***' },
  MIDDLE:    { label: '中段脱敏',   color: '#E89311', example: '110****1234' },
  TAIL:      { label: '尾部脱敏',   color: '#3b82f6', example: '13800****' },
  HEAD:      { label: '头部脱敏',   color: '#A855F7', example: '****1234' },
  CUSTOM:    { label: '自定义模板', color: '#7C2410', example: '按模板' },
  BAND_ONLY: { label: '仅显示薪等', color: '#E89311', example: '10-15K' },
}

export type FieldContext = 'self' | 'list' | 'export' | 'approval' | 'report' | 'detail'

export const FIELD_CONTEXT_META: Record<FieldContext, { label: string; color: string }> = {
  self:     { label: '自助查询', color: '#16a34a' },
  list:     { label: '列表',     color: '#3b82f6' },
  detail:   { label: '详情',     color: '#A855F7' },
  export:   { label: '导出',     color: '#C8141E' },
  approval: { label: '审批',     color: '#E89311' },
  report:   { label: '报表',     color: '#0EA5E9' },
}

export interface ContextMaskRule {
  context: FieldContext
  rule: MaskRule
  template?: string                       // CUSTOM 时模板
}

export interface FieldRule {
  id: string
  module: string
  field: string                           // 'idCard' / 'salary' / ...
  fieldLabel: string
  sensitivityLevel: SensitivityLevel
  visibleRoles: string[]                  // 哪些角色 code 能看
  editableRoles: string[]                 // 哪些角色 code 能改
  defaultMaskRule: MaskRule
  defaultMaskTemplate?: string
  contextMaskRules?: ContextMaskRule[]
  noExport: boolean
  audit: boolean
  notes?: string
}

// ─── 持久化 (UAT-FB#99 / ADR-001 2026-06-17): 走 sys_kv 后端表 ──
const SCHEMA_VERSION = 1

interface PersistState {
  schemaVersion: number
  rules: FieldRule[]
}

// ─── 种子: 30+ 条 ──────────────────────────────────────────────────────
let SEED_ID = 0
function mkRule(input: Omit<FieldRule, 'id'>): FieldRule {
  SEED_ID++
  return { id: `fr-${String(SEED_ID).padStart(4, '0')}`, ...input }
}

const SEED_RULES: FieldRule[] = [
  // ─── 员工模块 L4 ───
  mkRule({
    module: 'emp', field: 'idCard', fieldLabel: '身份证号',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'FACTORY_HR', 'ADMIN_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'CENTER_HR', 'ADMIN_SPEC'],
    defaultMaskRule: 'MIDDLE',
    contextMaskRules: [
      { context: 'list', rule: 'FULL' },
      { context: 'export', rule: 'FULL' },
      { context: 'self', rule: 'NONE' },
    ],
    noExport: false, audit: true,
    notes: '身份证依《个人信息保护法》, 列表/导出脱敏, 自助页可显示完整',
  }),
  mkRule({
    module: 'emp', field: 'bankAccount', fieldLabel: '银行卡号',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'PAYROLL_SPEC', 'FINANCE_LIAISON', 'EMP'],
    editableRoles: ['GROUP_HR', 'PAYROLL_SPEC', 'EMP'],
    defaultMaskRule: 'MIDDLE',
    contextMaskRules: [
      { context: 'list', rule: 'TAIL' },
      { context: 'export', rule: 'FULL' },
      { context: 'self', rule: 'NONE' },
    ],
    noExport: true, audit: true,
    notes: '银行卡变更需冷静期 + 双人复核',
  }),
  mkRule({
    module: 'emp', field: 'mobile', fieldLabel: '手机号',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'FACTORY_HR', 'DEPT_MGR', 'ADMIN_SPEC', 'HRBP_PROD', 'HRBP_SALES', 'HRBP_FUNC', 'RECRUIT_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'CENTER_HR', 'ADMIN_SPEC', 'EMP'],
    defaultMaskRule: 'MIDDLE',
    contextMaskRules: [
      { context: 'list', rule: 'MIDDLE' },
      { context: 'self', rule: 'NONE' },
    ],
    noExport: false, audit: true,
  }),
  // 2026-07-05 P0 Part A: 打卡机实体卡号 (与 empNo 解耦). 后端 Employee.cardNo 已 WRITE_ONLY,
  // 员工自助/明细/列表都不返, HR 编辑档案时才能绑. 员工自己不该看/改 (防伪造工卡刷别人打卡).
  mkRule({
    module: 'emp', field: 'cardNo', fieldLabel: '打卡卡号',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'FACTORY_HR', 'ADMIN_SPEC'],
    editableRoles: ['GROUP_HR', 'CENTER_HR', 'FACTORY_HR', 'ADMIN_SPEC'],
    defaultMaskRule: 'FULL',
    contextMaskRules: [
      { context: 'list', rule: 'FULL' },
      { context: 'export', rule: 'FULL' },
      { context: 'self', rule: 'FULL' },
    ],
    noExport: true, audit: true,
    notes: '后端已 WRITE_ONLY, 前端仅编辑态可填. 员工自助不可见 (防伪造工卡)',
  }),
  mkRule({
    module: 'emp', field: 'email', fieldLabel: '邮箱',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'FACTORY_HR', 'DEPT_MGR', 'ADMIN_SPEC', 'HRBP_PROD', 'HRBP_SALES', 'HRBP_FUNC', 'RECRUIT_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'CENTER_HR', 'EMP'],
    defaultMaskRule: 'MIDDLE',
    noExport: false, audit: false,
  }),
  mkRule({
    module: 'emp', field: 'homeAddress', fieldLabel: '家庭住址',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'ADMIN_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'EMP'],
    defaultMaskRule: 'MIDDLE',
    noExport: true, audit: true,
  }),
  mkRule({
    module: 'emp', field: 'emergencyContact', fieldLabel: '紧急联系人',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'ADMIN_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'EMP'],
    defaultMaskRule: 'MIDDLE',
    noExport: false, audit: false,
  }),
  mkRule({
    module: 'emp', field: 'birthday', fieldLabel: '出生日期',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'FACTORY_HR', 'DEPT_MGR', 'ADMIN_SPEC', 'HRBP_PROD', 'HRBP_SALES', 'HRBP_FUNC', 'EMP'],
    editableRoles: ['GROUP_HR', 'ADMIN_SPEC'],
    defaultMaskRule: 'HEAD',
    contextMaskRules: [{ context: 'self', rule: 'NONE' }],
    noExport: false, audit: false,
  }),
  mkRule({
    module: 'emp', field: 'maritalStatus', fieldLabel: '婚姻状况',
    sensitivityLevel: 'L2',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'ADMIN_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'EMP'],
    defaultMaskRule: 'NONE',
    noExport: false, audit: false,
  }),
  mkRule({
    module: 'emp', field: 'religion', fieldLabel: '宗教信仰',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'EMP'],
    editableRoles: ['EMP'],
    defaultMaskRule: 'FULL',
    contextMaskRules: [{ context: 'self', rule: 'NONE' }],
    noExport: true, audit: true,
    notes: '依《个人信息保护法》宗教属敏感个人信息',
  }),
  mkRule({
    module: 'emp', field: 'ethnicity', fieldLabel: '民族',
    sensitivityLevel: 'L2',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'ADMIN_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'ADMIN_SPEC'],
    defaultMaskRule: 'NONE',
    noExport: false, audit: false,
  }),
  mkRule({
    module: 'emp', field: 'healthCert', fieldLabel: '健康证号',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'FACTORY_HR', 'ADMIN_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'FACTORY_HR', 'ADMIN_SPEC'],
    defaultMaskRule: 'MIDDLE',
    noExport: false, audit: true,
    notes: '食品业必备 + 食安认证留档 6 年',
  }),

  // ─── 薪酬模块 L4 ───
  mkRule({
    module: 'pay', field: 'grossSalary', fieldLabel: '应发工资',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'PAYROLL_SPEC', 'COMP_BEN_SPEC', 'FINANCE_LIAISON', 'EMP'],
    editableRoles: ['GROUP_HR', 'PAYROLL_SPEC'],
    defaultMaskRule: 'BAND_ONLY',
    contextMaskRules: [
      { context: 'list', rule: 'BAND_ONLY' },
      { context: 'export', rule: 'FULL' },
      { context: 'self', rule: 'NONE' },
    ],
    noExport: false, audit: true,
    notes: '薪酬全额 L4, 跨公司视图限制 BAND_ONLY',
  }),
  mkRule({
    module: 'pay', field: 'netSalary', fieldLabel: '实发工资',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'PAYROLL_SPEC', 'FINANCE_LIAISON', 'EMP'],
    editableRoles: ['GROUP_HR', 'PAYROLL_SPEC'],
    defaultMaskRule: 'BAND_ONLY',
    contextMaskRules: [
      { context: 'export', rule: 'FULL' },
      { context: 'self', rule: 'NONE' },
    ],
    noExport: false, audit: true,
  }),
  mkRule({
    module: 'pay', field: 'baseSalary', fieldLabel: '基本工资',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'PAYROLL_SPEC', 'COMP_BEN_SPEC', 'FINANCE_LIAISON', 'EMP'],
    editableRoles: ['GROUP_HR', 'PAYROLL_SPEC'],
    defaultMaskRule: 'BAND_ONLY',
    contextMaskRules: [{ context: 'self', rule: 'NONE' }],
    noExport: false, audit: true,
  }),
  mkRule({
    module: 'pay', field: 'bonus', fieldLabel: '奖金',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'PAYROLL_SPEC', 'COMP_BEN_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'PAYROLL_SPEC'],
    defaultMaskRule: 'BAND_ONLY',
    contextMaskRules: [{ context: 'self', rule: 'NONE' }],
    noExport: false, audit: true,
  }),
  mkRule({
    module: 'pay', field: 'commission', fieldLabel: '提成',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'PAYROLL_SPEC', 'HRBP_SALES', 'EMP'],
    editableRoles: ['GROUP_HR', 'PAYROLL_SPEC'],
    defaultMaskRule: 'BAND_ONLY',
    contextMaskRules: [{ context: 'self', rule: 'NONE' }],
    noExport: false, audit: true,
  }),
  mkRule({
    module: 'pay', field: 'tax', fieldLabel: '个税',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'PAYROLL_SPEC', 'FINANCE_LIAISON', 'EMP'],
    editableRoles: ['GROUP_HR', 'PAYROLL_SPEC'],
    defaultMaskRule: 'FULL',
    contextMaskRules: [{ context: 'self', rule: 'NONE' }],
    noExport: false, audit: true,
    notes: '个税信息独立隔离, 个税申报员单独授权',
  }),
  mkRule({
    module: 'pay', field: 'salaryBand', fieldLabel: '薪等',
    sensitivityLevel: 'L2',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'FACTORY_HR', 'DEPT_MGR', 'PAYROLL_SPEC', 'COMP_BEN_SPEC', 'HRBP_PROD', 'HRBP_SALES', 'HRBP_FUNC', 'EMP'],
    editableRoles: ['GROUP_HR', 'PAYROLL_SPEC'],
    defaultMaskRule: 'NONE',
    noExport: false, audit: false,
  }),

  // ─── 社保模块 L4 ───
  mkRule({
    module: 'social', field: 'socialBase', fieldLabel: '社保基数',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'COMP_BEN_SPEC', 'PAYROLL_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'COMP_BEN_SPEC'],
    defaultMaskRule: 'BAND_ONLY',
    contextMaskRules: [{ context: 'self', rule: 'NONE' }],
    noExport: false, audit: true,
  }),
  mkRule({
    module: 'social', field: 'pensionAmount', fieldLabel: '养老金额',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'COMP_BEN_SPEC', 'PAYROLL_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'COMP_BEN_SPEC'],
    defaultMaskRule: 'FULL',
    contextMaskRules: [{ context: 'self', rule: 'NONE' }],
    noExport: false, audit: true,
  }),
  mkRule({
    module: 'social', field: 'fundBase', fieldLabel: '公积金基数',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'COMP_BEN_SPEC', 'PAYROLL_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'COMP_BEN_SPEC'],
    defaultMaskRule: 'BAND_ONLY',
    contextMaskRules: [{ context: 'self', rule: 'NONE' }],
    noExport: false, audit: true,
  }),

  // ─── 绩效模块 L3 ───
  mkRule({
    module: 'perf', field: 'finalGrade', fieldLabel: '最终绩效等级',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'FACTORY_HR', 'DEPT_MGR', 'HRBP_PROD', 'HRBP_SALES', 'HRBP_FUNC', 'EMP'],
    editableRoles: ['GROUP_HR', 'CENTER_HR', 'DEPT_MGR'],
    defaultMaskRule: 'NONE',
    contextMaskRules: [{ context: 'list', rule: 'FULL' }],
    noExport: false, audit: true,
    notes: '绩效信息内部敏感, 列表脱敏, 详情查看者审计',
  }),
  mkRule({
    module: 'perf', field: 'selfScore', fieldLabel: '自评分',
    sensitivityLevel: 'L2',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'DEPT_MGR', 'EMP'],
    editableRoles: ['EMP'],
    defaultMaskRule: 'NONE',
    noExport: false, audit: false,
  }),
  mkRule({
    module: 'perf', field: 'managerScore', fieldLabel: '上级评分',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'DEPT_MGR', 'EMP'],
    editableRoles: ['GROUP_HR', 'CENTER_HR', 'DEPT_MGR'],
    defaultMaskRule: 'NONE',
    contextMaskRules: [{ context: 'self', rule: 'NONE' }],
    noExport: false, audit: true,
  }),

  // ─── 招聘模块 ───
  mkRule({
    module: 'rec', field: 'expectedSalary', fieldLabel: '期望薪资',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'RECRUIT_SPEC', 'HRBP_PROD', 'HRBP_SALES', 'HRBP_FUNC'],
    editableRoles: ['GROUP_HR', 'RECRUIT_SPEC'],
    defaultMaskRule: 'BAND_ONLY',
    noExport: false, audit: true,
  }),
  mkRule({
    module: 'rec', field: 'candidatePhone', fieldLabel: '候选人手机',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'RECRUIT_SPEC', 'HRBP_PROD', 'HRBP_SALES', 'HRBP_FUNC'],
    editableRoles: ['RECRUIT_SPEC'],
    defaultMaskRule: 'MIDDLE',
    noExport: false, audit: true,
  }),
  mkRule({
    module: 'rec', field: 'candidateIdCard', fieldLabel: '候选人身份证',
    sensitivityLevel: 'L4',
    visibleRoles: ['GROUP_HR', 'RECRUIT_SPEC'],
    editableRoles: ['RECRUIT_SPEC'],
    defaultMaskRule: 'MIDDLE',
    contextMaskRules: [{ context: 'export', rule: 'FULL' }],
    noExport: true, audit: true,
  }),

  // ─── 流程 / 工号等 L1 ───
  mkRule({
    module: 'emp', field: 'empNo', fieldLabel: '工号',
    sensitivityLevel: 'L1',
    visibleRoles: [],   // [] = 全员可见
    editableRoles: ['GROUP_HR', 'ADMIN_SPEC'],
    defaultMaskRule: 'NONE',
    noExport: false, audit: false,
  }),
  mkRule({
    module: 'emp', field: 'empName', fieldLabel: '姓名',
    sensitivityLevel: 'L1',
    visibleRoles: [],
    editableRoles: ['GROUP_HR', 'ADMIN_SPEC'],
    defaultMaskRule: 'NONE',
    noExport: false, audit: false,
  }),
  mkRule({
    module: 'emp', field: 'position', fieldLabel: '岗位',
    sensitivityLevel: 'L1',
    visibleRoles: [],
    editableRoles: ['GROUP_HR', 'CENTER_HR', 'ADMIN_SPEC'],
    defaultMaskRule: 'NONE',
    noExport: false, audit: false,
  }),
  mkRule({
    module: 'emp', field: 'hireDate', fieldLabel: '入职日期',
    sensitivityLevel: 'L2',
    visibleRoles: [],
    editableRoles: ['GROUP_HR', 'ADMIN_SPEC'],
    defaultMaskRule: 'NONE',
    noExport: false, audit: false,
  }),

  // ─── 离职信息 ───
  mkRule({
    module: 'emp', field: 'offboardReason', fieldLabel: '离职原因',
    sensitivityLevel: 'L3',
    visibleRoles: ['GROUP_HR', 'CENTER_HR', 'FACTORY_HR', 'ADMIN_SPEC', 'EMP'],
    editableRoles: ['GROUP_HR', 'ADMIN_SPEC'],
    defaultMaskRule: 'NONE',
    contextMaskRules: [{ context: 'list', rule: 'FULL' }],
    noExport: true, audit: true,
  }),
]

// ─── State (UAT-FB#99): useBackendKv 自动 debounced PUT 到 sys_kv ──
const kv = useBackendKv<PersistState>('cfg:field_perm', {
  schemaVersion: SCHEMA_VERSION,
  rules: SEED_RULES,
}, {
  reject: (remote: any) => remote?.schemaVersion !== SCHEMA_VERSION,
})
const state = kv.state

function persist() { kv.save() }

if (state.rules.length === 0) {
  state.rules.push(...SEED_RULES)
  persist()
}

// ─── Getters ──────────────────────────────────────────────────────────────
const ruleCount = computed(() => state.rules.length)
const rulesByLevel = computed(() => {
  const m: Record<SensitivityLevel, number> = { L1: 0, L2: 0, L3: 0, L4: 0 }
  for (const r of state.rules) m[r.sensitivityLevel]++
  return m
})

// ─── CRUD ─────────────────────────────────────────────────────────────────
function listRules(opts?: {
  module?: string
  sensitivityLevel?: SensitivityLevel
  field?: string
  visibleToRole?: string
}): FieldRule[] {
  let rows = [...state.rules]
  if (opts?.module) rows = rows.filter(r => r.module === opts.module)
  if (opts?.sensitivityLevel) rows = rows.filter(r => r.sensitivityLevel === opts.sensitivityLevel)
  if (opts?.field) rows = rows.filter(r => r.field === opts.field)
  if (opts?.visibleToRole) rows = rows.filter(r => r.visibleRoles.length === 0 || r.visibleRoles.includes(opts.visibleToRole!))
  return rows
}

function getRule(module: string, field: string): FieldRule | undefined {
  return state.rules.find(r => r.module === module && r.field === field)
}

function getRulesForModule(module: string): FieldRule[] {
  return state.rules.filter(r => r.module === module)
}

function getRulesForRole(roleCode: string): FieldRule[] {
  return state.rules.filter(r => r.visibleRoles.length === 0 || r.visibleRoles.includes(roleCode))
}

function setRule(input: Omit<FieldRule, 'id'> & { id?: string }): FieldRule {
  const existing = state.rules.find(r => r.module === input.module && r.field === input.field)
  if (existing) {
    Object.assign(existing, input)
    persist()
    return existing
  }
  SEED_ID++
  const created: FieldRule = { id: input.id || `fr-${String(SEED_ID).padStart(4, '0')}`, ...input }
  state.rules.push(created)
  persist()
  return created
}

function updateRule(id: string, patch: Partial<FieldRule>): FieldRule | undefined {
  const r = state.rules.find(x => x.id === id)
  if (!r) return undefined
  Object.assign(r, patch)
  persist()
  return r
}

function deleteRule(id: string): boolean {
  const idx = state.rules.findIndex(r => r.id === id)
  if (idx === -1) return false
  state.rules.splice(idx, 1)
  persist()
  return true
}

function isFieldVisibleToRole(module: string, field: string, roleCode: string): boolean {
  const rule = getRule(module, field)
  if (!rule) return true                                            // 无规则默认可见
  if (rule.visibleRoles.length === 0) return true                   // 空 = 全员
  return rule.visibleRoles.includes(roleCode)
}

function isFieldEditableByRole(module: string, field: string, roleCode: string): boolean {
  const rule = getRule(module, field)
  if (!rule) return true
  if (rule.editableRoles.length === 0) return true
  return rule.editableRoles.includes(roleCode)
}

function getMaskRule(module: string, field: string, context: FieldContext): { rule: MaskRule; template?: string } {
  const r = getRule(module, field)
  if (!r) return { rule: 'NONE' }
  const ctxRule = r.contextMaskRules?.find(c => c.context === context)
  if (ctxRule) return { rule: ctxRule.rule, template: ctxRule.template }
  return { rule: r.defaultMaskRule, template: r.defaultMaskTemplate }
}

function reset() {
  const seed = JSON.parse(JSON.stringify(SEED_RULES)) as FieldRule[]
  state.rules.splice(0, state.rules.length, ...seed)
  persist()
}

// ─── 导出 ─────────────────────────────────────────────────────────────────
export const fieldPermissionStore = {
  state,
  ruleCount,
  rulesByLevel,
  listRules,
  getRule,
  getRulesForModule,
  getRulesForRole,
  setRule,
  updateRule,
  deleteRule,
  isFieldVisibleToRole,
  isFieldEditableByRole,
  getMaskRule,
  reset,
  // UAT-FB#99: 暴露同步状态
  loaded: kv.loaded,
  syncError: kv.error,
}

export default fieldPermissionStore
